import { useState } from 'react';
import { Table, Button } from 'reactstrap';
import StandardLayout from '@/common/layouts/StandardLayout';
import { FaPlus } from 'react-icons/fa';
import SettingRow from './components/SettingRow';
import EditSettingModal from './components/EditSettingModal';
import { GlobalSettingsDto, isSettingType, SettingCategory } from '@repo/common-types';
import { useGlobalSettingsByCategory, useUpdateGlobalSetting } from '@/services/settings/useSettingsQueries';

const TournamentSettingsPage = () => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<GlobalSettingsDto | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Use React Query hooks
  const { data: settings = [], isLoading: loading, error } = useGlobalSettingsByCategory(SettingCategory.TOURNAMENT);

  const updateGlobalSettingMutation = useUpdateGlobalSetting();

  const handleEdit = (setting: GlobalSettingsDto) => {
    setSelectedSetting(setting);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedSetting(null);
  };

  const handleSave = async (updatedSetting: GlobalSettingsDto) => {
    try {
      await updateGlobalSettingMutation.mutateAsync({
        key: updatedSetting.key,
        data: {
          value: updatedSetting.value,
          type: isSettingType(updatedSetting.type) ? updatedSetting.type : undefined,
          category: updatedSetting.category,
          description: updatedSetting.description,
        },
      });

      closeEditModal();
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error('Error updating setting:', error);
    }
  };

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
  };

  return (
    <StandardLayout pageTitle="Tournament Settings">
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="py-4 text-center">
              <div className="alert alert-danger">Failed to load settings. Please try again.</div>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Value</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {settings.map((setting) => (
                  <SettingRow key={setting.key} setting={setting} onEdit={handleEdit} />
                ))}
              </tbody>
            </Table>
          )}

          {!loading && !error && settings.length === 0 && (
            <div className="py-4 text-center">
              <p className="text-muted">No tournament settings found.</p>
            </div>
          )}
        </div>
      </div>

      {editModalOpen && selectedSetting && (
        <EditSettingModal
          setting={selectedSetting}
          onClose={closeEditModal}
          onSave={handleSave}
          formStatus={{
            isLoading: updateGlobalSettingMutation.isPending,
            error: updateGlobalSettingMutation.error?.message || null,
          }}
        />
      )}

      {/* TODO: Add CreateSettingModal component when needed */}
      {/* {createModalOpen && (
        <CreateSettingModal
          onClose={closeCreateModal}
          onSave={fetchSettings}
        />
      )} */}
    </StandardLayout>
  );
};

export default TournamentSettingsPage;
